import * as z from 'zod';

import { SftpKeyId, SftpSiteId } from '../BrandedIds';
import { SftpKeyAlgorithm } from '../keys/SftpKey';
import { zodSensitive } from '../secrets/Sensitive';

export const SftpAuthMode = z.enum(['PASSWORD', 'KEY']);
export type SftpAuthMode = z.infer<typeof SftpAuthMode>;

export const SftpSite = z.object({
  id: SftpSiteId,
  name: z.string(),
  hostname: z.string(),
  port: z.number(),
  path: z.string(),
  username: z.string(),
  authMode: SftpAuthMode,
  keyId: SftpKeyId.nullish(),
  hasPassword: z.boolean(),
});
export type SftpSite = z.infer<typeof SftpSite>;

export const SftpSiteWithSecrets = z.discriminatedUnion('authMode', [
  SftpSite.extend({
    authMode: z.literal('PASSWORD'),
    password: zodSensitive(z.string()).optional(),
    keyMaterial: z.undefined().optional(),
  }),
  SftpSite.extend({
    authMode: z.literal('KEY'),
    password: z.undefined().optional(),
    keyMaterial: z.object({
      // `z.custom` (not `z.instanceof(Buffer)`) keeps this file browser-safe.
      // Buffer subclasses Uint8Array, so the runtime check still passes
      // server-side where this schema is actually parsed.
      privateKey: zodSensitive(z.custom<Buffer>((val) => val instanceof Uint8Array, 'expected Buffer')),
      algorithm: SftpKeyAlgorithm,
    }),
  }),
]);
export type SftpSiteWithSecrets = z.infer<typeof SftpSiteWithSecrets>;

// `nullish` (not `optional`) because GraphQL codegen surfaces nullable
// inputs as `string | null | undefined`. The store defaults `authMode`
// to PASSWORD when omitted.
export const SftpSiteInput = z.object({
  name: z.string(),
  hostname: z.string(),
  port: z.number(),
  path: z.string(),
  username: z.string(),
  authMode: SftpAuthMode.nullish(),
  password: z.string().nullish(),
  keyId: SftpKeyId.nullish(),
});
export type SftpSiteInput = z.infer<typeof SftpSiteInput>;

export const UpdateSftpSiteInput = SftpSiteInput.extend({ id: SftpSiteId });
export type UpdateSftpSiteInput = z.infer<typeof UpdateSftpSiteInput>;
